import React, {useMemo} from "react";
import {Link} from "react-router-dom";
import {useSelector} from "react-redux";
import {currencyFormat} from "../../utils/masks";
import {useTranslation} from "react-i18next";
import {FaArrowDown, FaArrowUp, FaChartLine} from "react-icons/fa";
import DivDiagonal from "../../components/DivDiagonal";
import {
    StatsIntro,
    StatStack,
    StatItem,
    StatIconWrap,
    StatText,
    StatLabel,
    StatHint,
    StatValue,
} from "../../components/StatSummaryCard";
import {
    TradingWrapper,
    ContentArea,
    TradingListPageHeader,
    HeaderActions,
    NewTradingButton,
    TradingCard,
} from "../Trading/styles";
import styled from "styled-components";

const MetaViewNarrow = styled.div`
    max-width: 560px;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    position: relative;
    z-index: 2;
    margin-top: 16px;

    @media (min-width: 576px) {
        margin-top: 20px;
    }

    @media (min-width: 992px) {
        margin-top: 24px;
    }
`;

export default function ViewMeta() {
    const {meta, coinType, profitObtainedToday} = useSelector((state) => state.BankReducer);
    const { t } = useTranslation();

    const stopLossVal = parseFloat(meta ? meta.stopLoss : 0);
    const stopWinVal = parseFloat(meta ? meta.stopWin : 0);
    const profitVal = parseFloat(profitObtainedToday != null ? profitObtainedToday : 0);

    const profitColor = useMemo(() => {
        if (profitVal > 0) return "#8aedab";
        if (profitVal < 0) return "#ff9a9a";
        return "#e0e0e0";
    }, [profitVal]);

    return (
        <TradingWrapper>
            <DivDiagonal />
            <ContentArea>
                <TradingListPageHeader>
                    <h1>
                        <i className="fas fa-bullseye" aria-hidden="true"/> {t('goal_card_title')}
                    </h1>
                    <HeaderActions>
                        <NewTradingButton as={Link} to="/meta/save">
                            <i className="fas fa-edit"/> {t('edit')}
                        </NewTradingButton>
                    </HeaderActions>
                </TradingListPageHeader>

                <MetaViewNarrow>
                    <TradingCard>
                        <StatsIntro>{t('goal_view_intro')}</StatsIntro>
                        <StatStack>
                            <StatItem $accent="rgba(255, 107, 107, 0.85)">
                                <StatIconWrap
                                    $bg="rgba(255, 107, 107, 0.15)"
                                    $fg="#ff9a9a"
                                    $shadow="rgba(255, 107, 107, 0.2)"
                                >
                                    <FaArrowDown aria-hidden="true"/>
                                </StatIconWrap>
                                <StatText>
                                    <StatLabel>{t('goal_view_stop_loss')}</StatLabel>
                                    <StatHint>{t('goal_view_hint_stop_loss')}</StatHint>
                                </StatText>
                                <StatValue $c="#ff9a9a">
                                    {coinType} {currencyFormat(stopLossVal)}
                                </StatValue>
                            </StatItem>

                            <StatItem $accent="rgba(91, 211, 124, 0.85)">
                                <StatIconWrap
                                    $bg="rgba(91, 211, 124, 0.15)"
                                    $fg="#8aedab"
                                    $shadow="rgba(91, 211, 124, 0.2)"
                                >
                                    <FaArrowUp aria-hidden="true"/>
                                </StatIconWrap>
                                <StatText>
                                    <StatLabel>{t('goal_view_stop_win')}</StatLabel>
                                    <StatHint>{t('goal_view_hint_stop_win')}</StatHint>
                                </StatText>
                                <StatValue $c="#8aedab">
                                    {coinType} {currencyFormat(stopWinVal)}
                                </StatValue>
                            </StatItem>

                            <StatItem $accent="rgba(0, 212, 255, 0.75)">
                                <StatIconWrap
                                    $bg="rgba(0, 212, 255, 0.12)"
                                    $fg="#7ae8ff"
                                    $shadow="rgba(0, 212, 255, 0.18)"
                                >
                                    <FaChartLine aria-hidden="true"/>
                                </StatIconWrap>
                                <StatText>
                                    <StatLabel>{t('goal_profit_prejudice')}</StatLabel>
                                    <StatHint>{t('goal_view_hint_today')}</StatHint>
                                </StatText>
                                <StatValue $c={profitColor}>
                                    {coinType} {currencyFormat(profitVal)}
                                </StatValue>
                            </StatItem>
                        </StatStack>
                    </TradingCard>
                </MetaViewNarrow>
            </ContentArea>
        </TradingWrapper>
    );
}
